import {
  Button,
  Input,
  Skeleton,
  Typography,
} from '@growdash/design-system/atoms';
import { Brand, UniqueMenuItem } from '@growdash/growintel/generated';
import { CSVLinkProps } from '@growdash/growintel/types/csv';
import sortByField from '@growdash/growintel/utils/sort-by-field';
import { ClipboardList, Download, Search } from 'lucide-react';
import { useState } from 'react';
import { CSVLink as ReactCSVLink } from 'react-csv';
import BrandCard from './brand-card';
import MenuItems from './menu-items';

type BrandData = {
  brand: Brand | undefined;
  items: UniqueMenuItem[] | undefined;
} | null;

type Props = {
  isEmptyState?: boolean;
  firstBrandData?: BrandData;
  secondBrandData?: BrandData;
  isLoadingBrands?: boolean;
  refetchWatchlist?: () => void;
  isWatchlisted?: (brandId?: string) => boolean;
};

const filterItems = (items: UniqueMenuItem[], q: string) => {
  if (!q?.length) return sortByField(items, 'item_category');
  return items.filter(
    (item) =>
      item.item_name?.toLowerCase().includes(q.toLowerCase()) ||
      item.item_category?.toLowerCase().includes(q.toLowerCase()),
  );
};

const CSVLink = ReactCSVLink as unknown as React.ComponentType<CSVLinkProps>;

export default function MenuComparisonView({
  isEmptyState = false,
  secondBrandData,
  firstBrandData,
  isLoadingBrands = false,
  refetchWatchlist,
  isWatchlisted,
}: Props) {
  const [itemSearchKey, setItemSearchKey] = useState('');

  const csvData = [
    ...(firstBrandData?.items ?? []).map(
      ({
        scrape_month,
        aggregator,
        item_image,
        avg_price,
        min_price,
        max_price,
        ...rest
      }) => rest,
    ),
    ...(secondBrandData?.items ?? []).map(
      ({
        scrape_month,
        aggregator,
        item_image,
        avg_price,
        min_price,
        max_price,
        ...rest
      }) => rest,
    ),
  ];

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="col-span-1">
          {isLoadingBrands ? (
            <Skeleton className="h-12" />
          ) : (
            <BrandCard
              className="h-full"
              color="purple"
              isEmptyState={!firstBrandData?.brand}
              brand={firstBrandData?.brand}
              refetchWatchlist={refetchWatchlist}
              isWatchlisted={isWatchlisted}
            />
          )}
        </div>
        <div className="col-span-1">
          {isLoadingBrands ? (
            <Skeleton className="h-12" />
          ) : (
            <BrandCard
              className="h-full"
              isEmptyState={!secondBrandData?.brand}
              color="orange"
              brand={secondBrandData?.brand}
              refetchWatchlist={refetchWatchlist}
              isWatchlisted={isWatchlisted}
            />
          )}
        </div>
      </div>
      <div className="flex items-center space-x-2 mb-4 mt-6">
        <ClipboardList className=" text-primary" size={24} />

        <Typography className="text-lg font-semibold">
          Menu comparison
        </Typography>
      </div>
      <div
        className={`${
          isEmptyState ? 'opacity-50 pointer-events-none' : ''
        } flex items-center gap-4`}
      >
        <div className="rounded-lg relative">
          <Search className="w-4 h-4 text-tertiary absolute top-[50%] left-3 -translate-y-[50%]" />
          <Input
            onChange={(e) => {
              setItemSearchKey(e.target.value);
            }}
            type="text"
            placeholder="Search items"
            className="bg-transparent pb-1.5 h-[2.625rem] shadow-input pl-9 text-sm placeholder:text-tertiary"
          />
        </div>

        <div className="flex gap-4 ml-auto">
          <CSVLink data={csvData} filename="brand-menus.csv">
            <Button
              className="flex items-center justify-center bg-quarternary border border-secondary rounded-lg h-[2.125rem] px-2"
              variant="secondary"
            >
              <Download size={14} />
              Export
            </Button>
          </CSVLink>
        </div>
      </div>
      {isEmptyState ? (
        <Typography className="mt-4 mb-6 text-quarternary text-sm">
          Select restaurants to start comparing menu items, price and get
          customized insights
        </Typography>
      ) : null}
      <div
        className={`${
          isEmptyState ? 'hidden ' : ''
        } grid grid-cols-2 gap-4 mt-4`}
      >
        <div>
          {!isLoadingBrands && firstBrandData?.items && (
            <MenuItems
              items={filterItems(firstBrandData?.items, itemSearchKey)}
              isLoading={isLoadingBrands}
            />
          )}
        </div>
        <div>
          {!isLoadingBrands && secondBrandData?.items && (
            <MenuItems
              isCompetitorComponent
              items={filterItems(secondBrandData?.items, itemSearchKey)}
              isLoading={isLoadingBrands}
            />
          )}
        </div>
      </div>
    </div>
  );
}
